import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";

import {
  CssBaseline,
  Container,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Grid,
  Button,
  Box,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem
} from "@material-ui/core";

import {
  Visibility,
  Lens as LensIcon,
  VisibilityOff,
  CloudDownload,
  ArrowDropDown
} from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";

import { green, red, yellow } from "@material-ui/core/colors";

import { ROUTES } from "../../actions/app/constants";
import { setActiveRoute } from "../../actions/app/navigation";
import {
  fetchChangeLog,
  fetchChangeLogByIds,
  fetchChangeLogExportByDateRange,
  searchChangeLog,
  fetchChangeLogAll,
  fetchChangeLogExportAllByDateRange,
  searchChangeLogAll,
  fetchAllAdvertisers
} from "../../services/dcm/changeLog";
import {
  fetchAccountUserProfileService,
  fetchAccountUserProfilesByRoleIdService
} from "../../services/dcm/accountUserProfiles";

import { Searchbar } from "../../components/Shared/Searchbar/Searchbar";
import { ChangeLogFilter, DEFAULT_CHANGELOG_FILTERS } from "../../components/ChangeLog/ChangeLogFilter";
import HelperTooltip from "../../components/Shared/HelperTooltip/HelperTooltip";

import { searchDataByField, searchTableFields, exportChangeLogToExcel, buildCreativePreviewUrl, getCreativeTypeLabel } from "../../utils/dataUtils";
import { fetchSqlCreativesByIds } from "../../services/sql/creatives";
import { fetchCreativesByIds } from "../../services/dcm/creatives";
import { getCachedData, setCachedData } from "../../utils/cacheUtils";
import CreativePreviewDialog from "../../components/AssignedCreatives/CreativePreviewDialog";

const useStyles = makeStyles((theme) => ({
  route: {
    display: "flex",
    flexDirection: "column",
    height: "calc(90vh - 32px)",
    overflow: "hidden"
  },
  root: {
    width: "100%",
    paddingTop: theme.spacing(3)
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(2, 4, 0),
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  paperModal: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: "50%",
    overflow: "auto",
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  tableWrapper: {
    overflowX: "auto"
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  overdue: {
    color: red[500],
    fontWeight: 500,
    padding: "6px 8px 6px 16px"
  },
  tableCell: {
    padding: "6px 8px 6px 16px"
  },
  container: {
    flex: 1,
    overflow: "auto"
  },
  stripedRow: {
    "&:nth-of-type(even)": {
      backgroundColor: "rgba(0, 0, 0, 0.04)"
    }
  },
pagination: {
    marginLeft: -14
  },
  defaultGrid: { marginTop: "10px", marginBottom: "10px" },
  truncateCell: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  }
}));

const DATE_RANGE_OPTIONS = [
  { label: "Year to Date", value: "ytd" },
  { label: "6 Months",     value: "6months" },
  { label: "1 Year",       value: "1year" },
  { label: "2 Years",      value: "2years" }
];

const fetchChangeLogData = async () => {
  let currentAccountUserProfile = await fetchAccountUserProfileService();
  const isMasterAdmin = (currentAccountUserProfile.userRoleName || "").toLowerCase().includes("master admin");

  if (isMasterAdmin) {
    // Master Admins see all creative inserts (no user filter)
    const { changeLogs, nextPageToken } = await fetchChangeLogAll();
    return { changeLogData: changeLogs, profileEmailMap: {}, profileIds: [], nextPageToken, isMasterAdmin: true };
  }

  let profiles = await fetchAccountUserProfilesByRoleIdService(
    currentAccountUserProfile.userRoleId
  );

  const profileIds = profiles.map((p) => p.id);
  const profileEmailMap = profiles.reduce((map, p) => {
    map[p.id] = p.email;
    return map;
  }, {});

  const { changeLogs, nextPageToken } = await fetchChangeLogByIds(profileIds);

  return { changeLogData: changeLogs, profileEmailMap, profileIds, nextPageToken, isMasterAdmin: false };
}

const ChangeLog = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [changelog, setChangelog] = useState([]);
  const [profileEmailMap, setProfileEmailMap] = useState({});
  const [profileIds, setProfileIds] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [exportingDateRange, setExportingDateRange] = useState(null);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [advertiserMap, setAdvertiserMap] = useState({});
  const fetchedCreativeIdsRef = useRef(new Set());
  const fetchedSqlTypeIdsRef = useRef(new Set());
  const [dcmCreatives, setDcmCreatives] = useState([]);
  const [sqlCreativeTypes, setSqlCreativeTypes] = useState([]);
  const [previewModal, setPreviewModal] = useState({ open: false, url: null, name: null, creative: {}, previewType: "iframe" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [searchText, setSearchText] = useState("");
  const [serverSearchResults, setServerSearchResults] = useState(null);
  const [searchingServer, setSearchingServer] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_CHANGELOG_FILTERS);
  useEffect(() => {
    dispatch(setActiveRoute(ROUTES.CHANGE_LOG));
  }, [dispatch]);

  useEffect(() => {
    // Check sessionStorage cache first
    const cachedChangelog = getCachedData("changelog");
    if (cachedChangelog) {
      setChangelog(cachedChangelog.changeLogData);
      setProfileEmailMap(cachedChangelog.profileEmailMap);
      setProfileIds(cachedChangelog.profileIds);
      setNextPageToken(cachedChangelog.nextPageToken);
      setIsMasterAdmin(cachedChangelog.isMasterAdmin);
      // Restore SQL creative types from cache
      const cachedSql = getCachedData("sqlCreativeTypes");
      if (cachedSql) {
        setSqlCreativeTypes(cachedSql);
        cachedSql.forEach((row) => fetchedSqlTypeIdsRef.current.add(row.Creative_Id));
      }
      // dcmCreatives are not cached (creativeAssets too large for sessionStorage)
      // They'll be fetched per-page as the user navigates (backend node-cache avoids redundant GCM calls)
      // Restore advertiser map from cache
      const cachedAdvMap = getCachedData("advertiserMap");
      if (cachedAdvMap) setAdvertiserMap(cachedAdvMap);
      return;
    }

    setLoading(true);
    fetchChangeLogData().then(({ changeLogData, profileEmailMap, profileIds, nextPageToken, isMasterAdmin: isAdmin }) => {
      setChangelog(changeLogData);
      setProfileEmailMap(profileEmailMap);
      setProfileIds(profileIds);
      setNextPageToken(nextPageToken);
      setIsMasterAdmin(isAdmin);
      setLoading(false);
      // Cache the initial load (15 min TTL)
      setCachedData("changelog", { changeLogData, profileEmailMap, profileIds, nextPageToken, isMasterAdmin: isAdmin }, 15);
      // Fetch advertiser names for Master Admins
      if (isAdmin) {
        fetchAllAdvertisers().then((advertisers) => {
          const advMap = (advertisers || []).reduce((m, a) => { m[String(a.id)] = a.name; return m; }, {});
          setAdvertiserMap(advMap);
          setCachedData("advertiserMap", advMap, 240);
        }).catch(() => {});
      }
    }).catch((err) => {
      setError(err);
      setLoading(false);
    });
  }, []);

  // Fetch SQL creative types incrementally as changelog grows (needed for filters and type column)
  useEffect(() => {
    if (!changelog || changelog.length === 0) return;

    const allIds = [...new Set(changelog.map((log) => log.objectId))];
    const newIds = allIds.filter((id) => !fetchedSqlTypeIdsRef.current.has(id));

    if (newIds.length > 0) {
      newIds.forEach((id) => fetchedSqlTypeIdsRef.current.add(id));
      fetchSqlCreativesByIds(newIds).then((data) => {
        setSqlCreativeTypes((prev) => {
          const updated = [...prev, ...(data || [])];
          setCachedData("sqlCreativeTypes", updated, 30);
          return updated;
        });
      }).catch(() => {
        newIds.forEach((id) => fetchedSqlTypeIdsRef.current.delete(id));
      });
    }
  }, [changelog]);

  // Note: dcmCreatives are not cached in sessionStorage because creativeAssets
  // (needed for preview URL generation) are too large. Backend node-cache handles
  // deduplication of GCM API calls across page loads.

  const dcmCreativesMap = useMemo(() => {
    if (!dcmCreatives || dcmCreatives.length === 0) return {};
    return dcmCreatives.reduce((map, creative) => {
      map[creative.id] = creative;
      return map;
    }, {});
  }, [dcmCreatives]);

  const sqlCreativeTypeMap = useMemo(() => {
    if (!sqlCreativeTypes || sqlCreativeTypes.length === 0) return {};
    return sqlCreativeTypes.reduce((map, row) => {
      map[row.Creative_Id] = row.Type;
      return map;
    }, {});
  }, [sqlCreativeTypes]);

  const sqlCreativeStatusMap = useMemo(() => {
    if (!sqlCreativeTypes || sqlCreativeTypes.length === 0) return {};
    return sqlCreativeTypes.reduce((map, row) => {
      map[row.Creative_Id] = row.Status;
      return map;
    }, {});
  }, [sqlCreativeTypes]);

  const sqlCreativeHasActiveAdMap = useMemo(() => {
    if (!sqlCreativeTypes || sqlCreativeTypes.length === 0) return {};
    return sqlCreativeTypes.reduce((map, row) => {
      map[row.Creative_Id] = !!(
        row.Active_Standard_Ad_Assigned ||
        row.Active_Default_Ad_Assigned ||
        row.Active_Tracking_Ad_Assigned ||
        row.Active_ClickTracker_Ad_Assigned
      );
      return map;
    }, {});
  }, [sqlCreativeTypes]);

  const sqlAdvertiserIdMap = useMemo(() => {
    if (!sqlCreativeTypes || sqlCreativeTypes.length === 0) return {};
    return sqlCreativeTypes.reduce((map, row) => {
      map[row.Creative_Id] = row.Advertiser_Id;
      return map;
    }, {});
  }, [sqlCreativeTypes]);

  const creativePreviewMap = useMemo(() => {
    const map = {};

    if (dcmCreatives && dcmCreatives.length > 0) {
      dcmCreatives.forEach((creative) => {
        const built = buildCreativePreviewUrl(creative);
        if (built) {
          map[creative.id] = {
            url: built.url,
            name: creative.name,
            previewType: built.previewType
          };
        }
      });
    }

    return map;
  }, [dcmCreatives]);

  const filteredChangeLog = useMemo(() => {
    // Use server search results when available
    const source = serverSearchResults || changelog;
    if (!source || source.length === 0) return [];

    let result = searchTableFields(source, [
      "userProfileName",
      "objectId",
      "newValue"
    ], serverSearchResults ? "" : searchText);

    // Only show active creatives — prefer GCM active field, fallback to SQL Status
    // Show creatives whose status is unknown (not yet loaded) to avoid hiding the whole table during loading
    result = result.filter((l) => {
      const gcmCreative = dcmCreativesMap[l.objectId];
      if (gcmCreative && gcmCreative.active !== undefined) return gcmCreative.active === true;
      const status = sqlCreativeStatusMap[l.objectId];
      if (!status) return true; // Status not loaded yet — show until we know
      return status === "Active";
    });

    // Master Admin: only show creatives with at least one active ad assignment
    if (isMasterAdmin) {
      result = result.filter((l) => {
        const hasActiveAd = sqlCreativeHasActiveAdMap[l.objectId];
        if (hasActiveAd === undefined) return true; // SQL data not loaded yet — keep visible
        return hasActiveAd;
      });
    }

    if (filters.profileName !== "All") {
      result = result.filter((l) => {
        const email = profileEmailMap[l.userProfileId] || l.userProfileName;
        return email === filters.profileName;
      });
    }

    if (filters.creativeType !== "All") {
      result = result.filter((l) => {
        const dcmCreative = dcmCreativesMap[l.objectId];
        const dcmType = dcmCreative?.type;
        const name = dcmCreative?.name || (l.newValue ? l.newValue.slice(12) : "");
        const label = getCreativeTypeLabel(dcmType, sqlCreativeTypeMap[l.objectId], name);
        // Exclude unresolved types — only show exact matches
        return label === filters.creativeType;
      });
    }

    if (filters.advertiser !== "All") {
      result = result.filter((l) => {
        const advId = sqlAdvertiserIdMap[l.objectId] || dcmCreativesMap[l.objectId]?.advertiserId;
        const advName = advId ? advertiserMap[String(advId)] : null;
        return advName === filters.advertiser;
      });
    }

    if (filters.date !== "All") {
      result = result.filter(
        (l) => new Date(l.changeTime).toLocaleDateString() === filters.date
      );
    }

    return result;
  }, [searchText, changelog, serverSearchResults, filters, dcmCreativesMap, profileEmailMap, sqlCreativeTypeMap, sqlCreativeStatusMap, sqlCreativeHasActiveAdMap, advertiserMap, sqlAdvertiserIdMap, isMasterAdmin]);

  // Load GCM creative data (for previews) incrementally per visible page
  useEffect(() => {
    if (!filteredChangeLog || filteredChangeLog.length === 0) return;

    const start = page * rowsPerPage;
    const pageRows = filteredChangeLog.slice(start, start + rowsPerPage);
    const newIds = [...new Set(
      pageRows.map((log) => log.objectId).filter((id) => !fetchedCreativeIdsRef.current.has(id))
    )];

    if (newIds.length > 0) {
      newIds.forEach((id) => fetchedCreativeIdsRef.current.add(id));
      fetchCreativesByIds(newIds).then((data) => {
        setDcmCreatives((prev) => [...prev, ...(data || [])]);
      }).catch(() => {
        // Remove IDs on failure so they can be retried
        newIds.forEach((id) => fetchedCreativeIdsRef.current.delete(id));
      });
    }

    // Prefetch the next page in the background after a short delay
    const nextStart = start + rowsPerPage;
    if (nextStart < filteredChangeLog.length) {
      const timer = setTimeout(() => {
        const nextPageRows = filteredChangeLog.slice(nextStart, nextStart + rowsPerPage);
        const nextIds = [...new Set(
          nextPageRows.map((log) => log.objectId).filter((id) => !fetchedCreativeIdsRef.current.has(id))
        )];
        if (nextIds.length > 0) {
          nextIds.forEach((id) => fetchedCreativeIdsRef.current.add(id));
          fetchCreativesByIds(nextIds).then((data) => {
            setDcmCreatives((prev) => [...prev, ...(data || [])]);
          }).catch(() => {
            nextIds.forEach((id) => fetchedCreativeIdsRef.current.delete(id));
          });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [filteredChangeLog, page, rowsPerPage]);

  // Server-side search: when local search yields no results, search GCM
  useEffect(() => {
    if (!searchText || searchText.trim().length < 2 || (!isMasterAdmin && profileIds.length === 0)) {
      setServerSearchResults(null);
      return;
    }

    // Check if local search has results
    const localResults = searchTableFields(changelog, ["userProfileName", "objectId", "newValue"], searchText);
    if (localResults.length > 0) {
      setServerSearchResults(null);
      return;
    }

    setSearchingServer(true);
    const timer = setTimeout(() => {
      (isMasterAdmin ? searchChangeLogAll(searchText.trim()) : searchChangeLog(profileIds, searchText.trim()))
        .then(({ changeLogs }) => {
          if (changeLogs && changeLogs.length > 0) {
            setServerSearchResults(changeLogs);
            setPage(0);
            // Fetch creative data for server results
            const newIds = [...new Set(
              changeLogs.map((log) => log.objectId).filter((id) => !fetchedCreativeIdsRef.current.has(id))
            )];
            if (newIds.length > 0) {
              newIds.forEach((id) => fetchedCreativeIdsRef.current.add(id));
              fetchCreativesByIds(newIds).then((data) => {
                setDcmCreatives((prev) => [...prev, ...(data || [])]);
              }).catch(() => {});
            }
            // Fetch SQL types for server results (deduplicate against already-fetched IDs)
            const newSqlIds = [...new Set(
              changeLogs.map((log) => log.objectId).filter((id) => !fetchedSqlTypeIdsRef.current.has(id))
            )];
            if (newSqlIds.length > 0) {
              newSqlIds.forEach((id) => fetchedSqlTypeIdsRef.current.add(id));
              fetchSqlCreativesByIds(newSqlIds).then((data) => {
                setSqlCreativeTypes((prev) => [...prev, ...(data || [])]);
              }).catch(() => {});
            }
          } else {
            setServerSearchResults(null);
          }
        })
        .catch(() => setServerSearchResults(null))
        .finally(() => setSearchingServer(false));
    }, 600);

    return () => {
      clearTimeout(timer);
      setSearchingServer(false);
    };
  }, [searchText, changelog, profileIds, isMasterAdmin]);

  const handleLoadMore = useCallback(async () => {
    if (!nextPageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const { changeLogs, nextPageToken: newToken } = isMasterAdmin
        ? await fetchChangeLogAll(nextPageToken)
        : await fetchChangeLogByIds(profileIds, nextPageToken);
      setChangelog((prev) => {
        const updated = [...prev, ...changeLogs];
        setCachedData("changelog", { changeLogData: updated, profileEmailMap, profileIds, nextPageToken: newToken || null, isMasterAdmin }, 15);
        return updated;
      });
      setNextPageToken(newToken || null);
    } catch (err) {
      console.error("Error loading more changelog entries:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [nextPageToken, loadingMore, profileIds, isMasterAdmin]);

  // Auto-load remaining changelog pages in the background (up to 2 years)
  useEffect(() => {
    if (!nextPageToken || loading || loadingMore) return;

    // Stop auto-loading if the oldest loaded entry is older than 2 years
    if (changelog.length > 0) {
      const oldestEntry = changelog[changelog.length - 1];
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      if (oldestEntry.changeTime && new Date(oldestEntry.changeTime) < twoYearsAgo) {
        setAllDataLoaded(true);
        return;
      }
    }

    const timer = setTimeout(() => {
      handleLoadMore();
    }, 1500);
    return () => clearTimeout(timer);
  }, [nextPageToken, loading, loadingMore, handleLoadMore, changelog]);

  // Track when all data has been loaded (no more pages)
  useEffect(() => {
    if (nextPageToken === null && changelog.length > 0 && !loading) {
      setAllDataLoaded(true);
    }
  }, [nextPageToken, changelog.length, loading]);

  const handleDateRangeExport = useCallback(async (dateRange) => {
    setExportMenuAnchor(null);
    setExportingDateRange(dateRange);
    try {
      const changeLogs = isMasterAdmin
        ? await fetchChangeLogExportAllByDateRange(dateRange)
        : await fetchChangeLogExportByDateRange(profileIds, dateRange);
      const exportCreativeIds = [...new Set(changeLogs.map((l) => l.objectId))];
      const [exportCreatives, exportSqlTypes] = await Promise.all([
        fetchCreativesByIds(exportCreativeIds),
        fetchSqlCreativesByIds(exportCreativeIds)
      ]);
      const exportCreativesMap = (exportCreatives || []).reduce((map, c) => { map[c.id] = c; return map; }, {});
      const exportSqlTypeMap = (exportSqlTypes || []).reduce((map, row) => { map[row.Creative_Id] = row.Type; return map; }, {});
      const exportSqlAdvertiserMap = (exportSqlTypes || []).reduce((map, row) => { map[row.Creative_Id] = row.Advertiser_Id; return map; }, {});

      // Apply active filters to the export data
      let filteredLogs = changeLogs;
      if (filters.profileName !== "All") {
        filteredLogs = filteredLogs.filter((l) => profileEmailMap[l.userProfileId] === filters.profileName);
      }
      if (filters.creativeType !== "All") {
        filteredLogs = filteredLogs.filter((l) => {
          const dcmType = exportCreativesMap[l.objectId]?.type;
          const name = exportCreativesMap[l.objectId]?.name || (l.newValue ? l.newValue.slice(12) : "");
          return getCreativeTypeLabel(dcmType, exportSqlTypeMap[l.objectId], name) === filters.creativeType;
        });
      }
      if (filters.advertiser !== "All") {
        filteredLogs = filteredLogs.filter((l) => {
          const advId = exportSqlAdvertiserMap[l.objectId] || exportCreativesMap[l.objectId]?.advertiserId;
          const advName = advId ? advertiserMap[String(advId)] : null;
          return advName === filters.advertiser;
        });
      }

      const label = DATE_RANGE_OPTIONS.find((o) => o.value === dateRange)?.label || dateRange;
      const filterSuffix = filters.profileName !== "All" || filters.creativeType !== "All" ? "-filtered" : "";
      const filename = `creative-log-${label.replace(/\s+/g, "-").toLowerCase()}${filterSuffix}-${new Date().toISOString().slice(0, 10)}`;
      exportChangeLogToExcel(filename, filteredLogs, profileEmailMap, exportCreativesMap, exportSqlTypeMap, isMasterAdmin ? advertiserMap : null, isMasterAdmin ? exportSqlAdvertiserMap : null);
    } catch (err) {
      console.error("Error exporting changelog by date range:", err);
    } finally {
      setExportingDateRange(null);
    }
  }, [profileIds, profileEmailMap, filters, isMasterAdmin, advertiserMap]);

  const isLastPage = page >= Math.ceil(changelog.length / rowsPerPage) - 1 && changelog.length > 0;

  return (
    <div className={classes.route}>
      <CssBaseline />
      <Container maxWidth="xl" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* <PlacementList /> */}
        <Grid container alignItems="center" justifyContent="center" spacing={2} style={{ flexShrink: 0 }}>
          <Grid item xs={6}>
            <h1>Creative Log</h1>
          </Grid>
          <Grid item xs={6}>
            <Box display="flex" justifyContent={"flex-end"}>
              <Tooltip title="Download current view as Excel">
                <IconButton onClick={() => exportChangeLogToExcel(`creative-log-${new Date().toISOString().slice(0, 10)}`, filteredChangeLog, profileEmailMap, dcmCreativesMap, sqlCreativeTypeMap, isMasterAdmin ? advertiserMap : null, isMasterAdmin ? sqlAdvertiserIdMap : null)}>
                  <CloudDownload />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export full date range as Excel">
                <IconButton onClick={(e) => setExportMenuAnchor(e.currentTarget)} disabled={exportingDateRange !== null}>
                  {exportingDateRange ? <CircularProgress size={22} /> : <><CloudDownload /><ArrowDropDown style={{ marginLeft: -6 }} /></>}
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {DATE_RANGE_OPTIONS.map(({ label, value }) => (
                  <MenuItem key={value} onClick={() => handleDateRangeExport(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Menu>
              <Searchbar
                table="Creative Log"
                onSearch={(e) => {setSearchText(e.target.value); setPage(0);}}
                onClear={() => {setSearchText("")}}
                searchTerm={searchText}
              />
              {searchingServer && <CircularProgress size={18} style={{ alignSelf: "center", marginLeft: 4, marginRight: 4 }} />}
              <HelperTooltip tooltipText="To search for multiple terms, separate with a comma. If no local results are found, a search is made against GCM." />
              <ChangeLogFilter
                changelog={changelog}
                dcmCreativesMap={dcmCreativesMap}
                sqlCreativeTypeMap={sqlCreativeTypeMap}
                profileEmailMap={profileEmailMap}
                filters={filters}
                setFilters={(f) => { setFilters(f); setPage(0); }}
                allDataLoaded={allDataLoaded}
                isMasterAdmin={isMasterAdmin}
                advertiserMap={advertiserMap}
                sqlAdvertiserIdMap={sqlAdvertiserIdMap}
              />
            </Box>
          </Grid>
        </Grid>
        <Box style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", paddingTop: "8px" }}>
          <Paper style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <TableContainer className={classes.container}>
                <Table size="small" style={{ tableLayout: "fixed", width: "100%" }}>
                  <colgroup>
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "5%" }} />
                    <col />
                    <col style={{ width: isMasterAdmin ? "8%" : "10%" }} />
                    {isMasterAdmin && <col style={{ width: "14%" }} />}
                    <col style={{ width: 180 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell>{isMasterAdmin ? "Profile" : "Email"}</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Preview</TableCell>
                      <TableCell>Creative Name</TableCell>
                      <TableCell>Creative Type</TableCell>
                      {isMasterAdmin && <TableCell>Advertiser</TableCell>}
                      <TableCell>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={isMasterAdmin ? 7 : 6} style={{ border: 0, height: "50vh" }}>
                          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <CircularProgress />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={isMasterAdmin ? 7 : 6} align="center" style={{ padding: "48px 16px" }}>
                          <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 12 }}>
                            <span style={{ color: "rgba(0,0,0,0.54)" }}>
                              Unable to load the Creative Log. Please check your VPN connection and try again.
                            </span>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setError(null);
                                setLoading(true);
                                fetchChangeLogData().then(({ changeLogData, profileEmailMap, profileIds, nextPageToken, isMasterAdmin: isAdmin }) => {
                                  setChangelog(changeLogData);
                                  setProfileEmailMap(profileEmailMap);
                                  setProfileIds(profileIds);
                                  setNextPageToken(nextPageToken);
                                  setIsMasterAdmin(isAdmin);
                                  setLoading(false);
                                  if (isAdmin) {
                                    fetchAllAdvertisers().then((advertisers) => {
                                      setAdvertiserMap((advertisers || []).reduce((m, a) => { m[String(a.id)] = a.name; return m; }, {}));
                                    }).catch(() => {});
                                  }
                                }).catch((err) => {
                                  setError(err);
                                  setLoading(false);
                                });
                              }}
                            >
                              Retry
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredChangeLog.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log, i, pageRows) => {
                        const email = profileEmailMap[log.userProfileId] || log.userProfileName;
                        return (
                        <TableRow key={log.id} className={classes.stripedRow}>
                          <TableCell className={classes.truncateCell}>
                            <Tooltip title={email} enterDelay={500}><span>{email}</span></Tooltip>
                          </TableCell>
                          <TableCell>{log.objectId}</TableCell>
                          <TableCell align="center">
                            {(() => {
                              const preview = creativePreviewMap[log.objectId];
                              const isRichMedia = preview?.previewType === "iframe" && dcmCreativesMap[log.objectId]?.type?.startsWith("RICH_MEDIA_");
                              const hidePreview = !preview || (!isMasterAdmin && isRichMedia);
                              const tooltipText = !preview ? "No preview available" : (!isMasterAdmin && isRichMedia) ? "Rich media preview not available" : "Preview creative";
                              return (
                            <Tooltip title={tooltipText}>
                              <span>
                                <IconButton
                                  disabled={hidePreview}
                                  onClick={() => {
                                    const preview = creativePreviewMap[log.objectId];
                                    if (preview?.url) {
                                      setPreviewModal({
                                        open: true,
                                        url: preview.url,
                                        name: preview.name,
                                        creative: dcmCreativesMap[log.objectId] || {},
                                        previewType: preview.previewType || "iframe",
                                        creativeName: dcmCreativesMap[log.objectId]?.name || (log.newValue ? log.newValue.slice(12) : ""),
                                        creativeId: log.objectId,
                                        creativeType: getCreativeTypeLabel(dcmCreativesMap[log.objectId]?.type, sqlCreativeTypeMap[log.objectId], dcmCreativesMap[log.objectId]?.name || (log.newValue ? log.newValue.slice(12) : "")),
                                        email: profileEmailMap[log.userProfileId] || log.userProfileName,
                                        timestamp: log.changeTime
                                      });
                                    }
                                  }}
                                >
                                  {creativePreviewMap[log.objectId] ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </span>
                            </Tooltip>
                              );
                            })()}
                          </TableCell>
                          <TableCell style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {dcmCreativesMap[log.objectId]?.name || log.newValue.slice(12)}
                          </TableCell>
                          <TableCell>{getCreativeTypeLabel(dcmCreativesMap[log.objectId]?.type, sqlCreativeTypeMap[log.objectId], dcmCreativesMap[log.objectId]?.name || (log.newValue ? log.newValue.slice(12) : ""))}</TableCell>
                          {isMasterAdmin && (
                            <TableCell className={classes.truncateCell}>
                              <Tooltip title={advertiserMap[String(sqlAdvertiserIdMap[log.objectId])] || advertiserMap[String(dcmCreativesMap[log.objectId]?.advertiserId)] || ""} enterDelay={500}>
                                <span>{advertiserMap[String(sqlAdvertiserIdMap[log.objectId])] || advertiserMap[String(dcmCreativesMap[log.objectId]?.advertiserId)] || ""}</span>
                              </Tooltip>
                            </TableCell>
                          )}
                          <TableCell style={{ whiteSpace: "nowrap" }}>
                            {new Date(log.changeTime).toLocaleString()}
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {nextPageToken && !allDataLoaded && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  py={0.5}
                  style={{ borderBottom: "1px solid rgba(224, 224, 224, 1)", gap: 8 }}
                >
                  <CircularProgress size={14} />
                  <span style={{ fontSize: 12, color: "#888" }}>Loading more data...</span>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={filteredChangeLog.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={(event, newPage) => setPage(newPage)}
                onChangeRowsPerPage={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </Paper>
          </Box>
      </Container>
      <CreativePreviewDialog
        setOpen={() => {}}
        previewDialogOpen={previewModal.open}
        setPreviewDialogOpen={(val) => setPreviewModal((prev) => ({ ...prev, open: val }))}
        creativePreviewURL={previewModal.url}
        selectedCreativePreview={previewModal.creative}
        previewType={previewModal.previewType || "iframe"}
        creativeName={previewModal.creativeName}
        creativeId={previewModal.creativeId}
        creativeType={previewModal.creativeType}
        email={previewModal.email}
        timestamp={previewModal.timestamp}
      />
    </div>
  );
};

export default ChangeLog;
